import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Sparkles, Wand2, Play, Loader2, Copy, Trash2, Send, Languages, Coffee, UtensilsCrossed,
  Hotel, Briefcase, Star, Film, Video
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const VERTICAL_ICONS = {
  coffee: Coffee,
  cafe: Coffee,
  restaurant: UtensilsCrossed,
  hotel: Hotel,
  any: Briefcase,
};

const LANG_FLAGS = { ar: '🇸🇦', en: '🇬🇧' };

const TemplateCard = ({ tpl, onPick }) => {
  const Icon = VERTICAL_ICONS[tpl.vertical] || Briefcase;
  const isAr = tpl.language === 'ar';
  return (
    <Card className="hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer" data-testid={`tpl-${tpl.id}`} onClick={() => onPick(tpl)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon className="h-4.5 w-4.5 text-emerald-600" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                <span>{LANG_FLAGS[tpl.language]}</span>
                <span>{tpl.vertical}</span>
              </div>
              <div className="text-xs text-gray-500">{tpl.duration_seconds}s · {tpl.channel_hint}</div>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50">
            {tpl.format}
          </Badge>
        </div>
        <h3 className={`font-semibold text-gray-900 leading-snug ${isAr ? 'text-right font-serif' : ''}`}>{tpl.title}</h3>
        {!isAr && tpl.title_en && tpl.title !== tpl.title_en && (
          <p className="text-xs text-gray-400 mt-1">{tpl.title_en}</p>
        )}
        <div className="mt-3 flex flex-wrap gap-1">
          {tpl.slots.map((s) => (
            <Badge key={s} variant="outline" className="text-[10px] font-mono text-gray-500">{s}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ScriptCard = ({ script, onDelete, onSendToProducer, onTurnIntoVideo }) => (
  <Card data-testid={`script-${script.id}`}>
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="text-sm">{script.template_title}</CardTitle>
          <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-2">
            <span>{LANG_FLAGS[script.language] || ''}</span>
            <span>{script.vertical}</span>
            <span>·</span>
            <span>{script.duration_seconds}s</span>
            <span>·</span>
            <span>{new Date(script.created_at).toLocaleDateString()}</span>
            {script.sent_to_producer_at && (
              <>
                <span>·</span>
                <span className="text-emerald-600">sent to producer ✓</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => onTurnIntoVideo(script)}
            className="px-2 py-1 text-[10px] font-medium rounded-md bg-emerald-brand text-white hover:bg-emerald-700 flex items-center gap-1"
            title="Generate an AI avatar video from this script — right inside Konnect"
            data-testid={`script-${script.id}-to-video`}
          >
            <Film className="h-3 w-3" />
            <span>Turn into Video</span>
          </button>
          <button
            onClick={() => { navigator.clipboard.writeText(script.script); toast.success('Copied'); }}
            className="p-1.5 text-gray-400 hover:text-gray-700"
            title="Copy script"
            data-testid={`script-${script.id}-copy`}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onSendToProducer(script)}
            className="p-1.5 text-gray-400 hover:text-emerald-600"
            title="Email shot list + brand brief to a studio/producer"
            data-testid={`script-${script.id}-send-producer`}
          >
            <Send className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(script.id)}
            className="p-1.5 text-gray-400 hover:text-rose-600"
            title="Delete"
            data-testid={`script-${script.id}-delete`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <pre className={`whitespace-pre-wrap text-xs text-gray-700 leading-relaxed ${script.language === 'ar' ? 'text-right font-sans' : 'font-mono'}`}>
        {script.script}
      </pre>
    </CardContent>
  </Card>
);

export default function InfluencerPage() {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [filterLang, setFilterLang] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [picked, setPicked] = useState(null);
  const [slots, setSlots] = useState({});
  const [extraNotes, setExtraNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [latest, setLatest] = useState(null);
  const navigate = useNavigate();

  // v3.3 (2026-07-01): pipe a generated script directly into the Avatar Studio
  // — no more copy-paste dead-end after script generation. The script is stashed
  // in sessionStorage under a well-known key that Creative Studio picks up on load.
  const turnIntoVideo = useCallback((s) => {
    try {
      sessionStorage.setItem('konnect.pending_avatar_script', JSON.stringify({
        script: s.script,
        title: s.template_title,
        language: s.language,
        duration_seconds: s.duration_seconds || 30,
        source: 'influencer_studio',
        script_id: s.id,
      }));
      toast.success('Script sent to Creative Studio — pick your avatar to render the video.');
      navigate('/content?tab=avatar');
    } catch (err) {
      toast.error('Could not hand off script — try Copy then paste in Avatar Studio.');
    }
  }, [navigate]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/influencer/templates`, {
        params: { language: filterLang || undefined, industry: filterIndustry || undefined },
      });
      setTemplates(r.data?.templates || []);
    } catch { /* ignore */ }
  }, [filterLang, filterIndustry]);

  const loadScripts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/influencer/scripts`);
      setScripts(r.data?.scripts || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadScripts(); }, [loadScripts]);

  const pick = (tpl) => {
    setPicked(tpl);
    setSlots(Object.fromEntries(tpl.slots.map((s) => [s, ''])));
    setExtraNotes('');
    setLatest(null);
  };

  const generate = async () => {
    if (!picked) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/influencer/generate`, {
        template_id: picked.id,
        slots,
        extra_notes: extraNotes || null,
      });
      setLatest(r.data);
      loadScripts();
      toast.success('Script generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate script');
    } finally {
      setGenerating(false);
    }
  };

  const deleteScript = async (id) => {
    if (!window.confirm('Delete this script?')) return;
    try {
      await axios.delete(`${API}/api/influencer/scripts/${id}`);
      setScripts((s) => s.filter((x) => x.id !== id));
    } catch { /* ignore */ }
  };

  const [producerOpen, setProducerOpen] = useState(false);
  const [producerScript, setProducerScript] = useState(null);
  const [producerEmail, setProducerEmail] = useState('');
  const [producerNotes, setProducerNotes] = useState('');
  const [sendingProducer, setSendingProducer] = useState(false);

  const openProducerDialog = (script) => {
    setProducerScript(script);
    setProducerEmail('');
    setProducerNotes('');
    setProducerOpen(true);
  };

  const sendToProducer = async () => {
    if (!producerScript || !producerEmail.trim()) return;
    setSendingProducer(true);
    try {
      await axios.post(`${API}/api/influencer/send-to-producer`, {
        script_id: producerScript.id,
        producer_email: producerEmail.trim(),
        extra_notes: producerNotes.trim(),
      });
      toast.success(`Brief emailed to ${producerEmail}`);
      setProducerOpen(false);
      // Refresh so the "sent to producer ✓" badge appears
      const { data } = await axios.get(`${API}/api/influencer/scripts`);
      setScripts(data.scripts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send brief');
    } finally {
      setSendingProducer(false);
    }
  };

  return (
    <div className="page-shell-wide" data-testid="influencer-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Video className="h-7 w-7 text-emerald-500" /> AI Influencer Studio<PageInfoTooltip pageKey="influencer" />
          </h1>
          <p className="text-gray-500 mt-1">Pick a template, fill the brief, and our AI Gateway writes a shoot-ready script in your brand voice.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="templates" data-testid="influencer-tab-templates">
            <Film className="h-4 w-4 mr-2" /> Templates
          </TabsTrigger>
          <TabsTrigger value="library" data-testid="influencer-tab-library">
            <Star className="h-4 w-4 mr-2" /> My scripts {scripts.length > 0 && <span className="ml-1.5 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{scripts.length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="mt-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-1 bg-white border rounded-lg p-1 text-sm">
              {[
                { id: '', label: 'All languages' },
                { id: 'ar', label: '🇸🇦 العربية' },
                { id: 'en', label: '🇬🇧 English' },
              ].map((o) => (
                <button
                  key={o.id || 'all'}
                  onClick={() => setFilterLang(o.id)}
                  className={`px-3 py-1.5 rounded-md font-medium ${filterLang === o.id ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  data-testid={`lang-filter-${o.id || 'all'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-white border rounded-lg p-1 text-sm">
              {[
                { id: '', label: 'All industries' },
                { id: 'horeca', label: 'HoReCa' },
              ].map((o) => (
                <button
                  key={o.id || 'all-i'}
                  onClick={() => setFilterIndustry(o.id)}
                  className={`px-3 py-1.5 rounded-md font-medium ${filterIndustry === o.id ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {templates.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-gray-400"><Sparkles className="h-10 w-10 mx-auto mb-3 text-gray-300" />No templates match your filters.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((t) => <TemplateCard key={t.id} tpl={t} onPick={pick} />)}
            </div>
          )}
        </TabsContent>

        {/* LIBRARY */}
        <TabsContent value="library" className="mt-6">
          {scripts.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-gray-400">
              <Star className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No scripts yet — pick a template to generate your first.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {scripts.map((s) => <ScriptCard key={s.id} script={s} onDelete={deleteScript} onSendToProducer={openProducerDialog} onTurnIntoVideo={turnIntoVideo} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* GENERATE MODAL */}
      <Dialog open={!!picked} onOpenChange={(o) => { if (!o) { setPicked(null); setLatest(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="generate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-emerald-600" /> {picked?.title_en || picked?.title}
            </DialogTitle>
          </DialogHeader>

          {!latest && picked && (
            <div className="space-y-4">
              <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                <Badge variant="outline">{LANG_FLAGS[picked.language]} {picked.language.toUpperCase()}</Badge>
                <Badge variant="outline">{picked.duration_seconds}s</Badge>
                <Badge variant="outline">{picked.format}</Badge>
                <Badge variant="outline">{picked.channel_hint}</Badge>
              </div>

              {picked.slots.map((slotKey) => (
                <div key={slotKey}>
                  <Label htmlFor={slotKey} className="text-xs uppercase tracking-wider text-gray-500">{slotKey.replace(/_/g, ' ')}</Label>
                  <Input
                    id={slotKey}
                    value={slots[slotKey] || ''}
                    onChange={(e) => setSlots((p) => ({ ...p, [slotKey]: e.target.value }))}
                    placeholder={`Enter ${slotKey.replace(/_/g, ' ')}`}
                    className="mt-1.5"
                    data-testid={`slot-${slotKey}`}
                  />
                </div>
              ))}

              <div>
                <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-gray-500">Extra notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={extraNotes}
                  onChange={(e) => setExtraNotes(e.target.value)}
                  placeholder="e.g. Mention our 30% Ramadan offer; avoid the word 'cheap'…"
                  className="mt-1.5"
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPicked(null)}>Cancel</Button>
                <Button
                  onClick={generate}
                  disabled={generating}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="generate-script-btn"
                >
                  {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                  {generating ? 'Writing…' : 'Generate script'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {latest && (
            <div className="space-y-4" data-testid="generated-script">
              <div className="bg-emerald-50/50 border border-emerald-200 rounded-lg p-4">
                <pre className={`whitespace-pre-wrap text-sm leading-relaxed ${latest.language === 'ar' ? 'text-right' : ''}`}>
                  {latest.script}
                </pre>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setLatest(null)}>Generate another</Button>
                <Button
                  variant="outline"
                  onClick={() => { navigator.clipboard.writeText(latest.script); toast.success('Copied'); }}
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy
                </Button>
                <Button onClick={() => { setPicked(null); setLatest(null); setTab('library'); }} className="bg-emerald-600 hover:bg-emerald-700">
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={producerOpen} onOpenChange={setProducerOpen}>
        <DialogContent className="sm:max-w-md" data-testid="send-producer-modal">
          <DialogHeader>
            <DialogTitle>Email this brief to a producer</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 leading-relaxed">
            Konnect will package the script + shot list + brand tone into a clean
            HTML email and send it to your chosen studio. Replies come back to your inbox.
          </p>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs">Producer email</Label>
              <Input
                type="email"
                value={producerEmail}
                onChange={(e) => setProducerEmail(e.target.value)}
                placeholder="producer@studio.com"
                data-testid="producer-email-input"
              />
            </div>
            <div>
              <Label className="text-xs">Notes for the producer (optional)</Label>
              <Textarea
                value={producerNotes}
                onChange={(e) => setProducerNotes(e.target.value)}
                placeholder="Delivery deadline, talent preferences, file format..."
                rows={3}
                data-testid="producer-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProducerOpen(false)}>Cancel</Button>
            <Button
              onClick={sendToProducer}
              disabled={!producerEmail.trim() || sendingProducer}
              data-testid="producer-send-btn"
            >
              {sendingProducer
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Send brief
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
